import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const API = "/api/auditoria/criterio";
const API_TIPOS = "/api/auditoria/tipo-documento";

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const Auditoria_ListarCriterio = () => {
  const navigate = useNavigate();

  const [criterios, setCriterios] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [buscar, setBuscar] = useState("");
  const [loading, setLoading] = useState(false);

  const manejarError = useCallback(
    (error, mensaje) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        Swal.fire("Sesión expirada", "Inicia sesión nuevamente.", "warning").then(
          () => navigate("/login")
        );
        return;
      }

      Swal.fire(
        "Error",
        error?.response?.data?.message || mensaje || "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );

  const cargar = useCallback(async () => {
    try {
      setLoading(true);

      const [resCriterios, resTipos] = await Promise.all([
        axios.get(API, authConfig()),
        axios.get(API_TIPOS, authConfig()),
      ]);

      setCriterios(
        Array.isArray(resCriterios.data?.criterios)
          ? resCriterios.data.criterios
          : []
      );

      setTipos(
        Array.isArray(resTipos.data?.tiposDocumento)
          ? resTipos.data.tiposDocumento
          : []
      );
    } catch (error) {
      manejarError(error, "No se pudieron cargar los criterios.");
    } finally {
      setLoading(false);
    }
  }, [manejarError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const filtrados = useMemo(() => {
    const texto = buscar.trim().toLowerCase();

    return criterios.filter((item) => {
      const tipoId =
        item.tipoDocumentoObjectId?._id || item.tipoDocumentoObjectId || "";

      const coincideTipo =
        !tipoFiltro || String(tipoId) === String(tipoFiltro);

      const coincideTexto =
        !texto ||
        [
          item.codigoCriterio,
          item.nombreCriterio,
          item.descripcionCriterio,
          item.campoDocumento,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(texto);

      return coincideTipo && coincideTexto;
    });
  }, [criterios, buscar, tipoFiltro]);

  const eliminar = async (item) => {
    const confirmar = await Swal.fire({
      title: "¿Eliminar criterio?",
      text: item.nombreCriterio,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmar.isConfirmed) return;

    try {
      await axios.delete(`${API}/${item._id}`, authConfig());
      await cargar();
      Swal.fire("Correcto", "Criterio eliminado.", "success");
    } catch (error) {
      manejarError(error, "No se pudo eliminar el criterio.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-1">Criterios de auditoría</h3>
          <p className="text-muted mb-0">
            Puntos que debe validar el auditor para cada tipo documental.
          </p>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => navigate("/auditoria/criterio/nuevo")}
        >
          Registrar criterio
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-7">
              <input
                className="form-control"
                placeholder="Buscar criterio..."
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
              />
            </div>

            <div className="col-md-5">
              <select
                className="form-select"
                value={tipoFiltro}
                onChange={(e) => setTipoFiltro(e.target.value)}
              >
                <option value="">Todos los tipos de documento</option>
                {tipos.map((tipo) => (
                  <option key={tipo._id} value={tipo._id}>
                    {tipo.nombreTipoDocumento}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Orden</th>
                  <th>Tipo documento</th>
                  <th>Código</th>
                  <th>Criterio</th>
                  <th>Campo</th>
                  <th>Obligatorio</th>
                  <th>Estado</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      Cargando...
                    </td>
                  </tr>
                ) : filtrados.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      No hay criterios registrados.
                    </td>
                  </tr>
                ) : (
                  filtrados.map((item) => (
                    <tr key={item._id}>
                      <td>{item.ordenCriterio ?? 0}</td>
                      <td>
                        {item.tipoDocumentoObjectId?.nombreTipoDocumento || "—"}
                      </td>
                      <td>{item.codigoCriterio || "—"}</td>
                      <td>{item.nombreCriterio || "—"}</td>
                      <td>
                        <code>{item.campoDocumento || "—"}</code>
                      </td>
                      <td>{item.obligatorioCriterio ? "Sí" : "No"}</td>
                      <td>{item.estadoCriterio || "—"}</td>
                      <td className="text-end">
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              navigate(
                                `/auditoria/criterio/editar/${item._id}`
                              )
                            }
                          >
                            Editar
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => eliminar(item)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auditoria_ListarCriterio;
